import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Typography,
} from "@mui/material";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import { TableCellType } from "components/LazyTable";
import SortableTable from "components/SortableTable";
import useGlobalContext from "hooks/globalData";
import { useState } from "react";
import { useIntl } from "react-intl";
import {
  IRequirementAuthoringOptions,
  IRequirementNoteDetail,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import ensureOk from "./ensureOk";
import { noteTypeLabelKey } from "./lookupEnums";
import NoteDialog from "./NoteDialog";
import RowActionsCell from "./RowActionsCell";
import styles from "./styles";

type NotesSectionProps = {
  requirementSetId: string;
  /** Set for the notes of a requirement; absent for the notes of the set itself. */
  requirementId?: string;
  notes: IRequirementNoteDetail[];
  options?: IRequirementAuthoringOptions;
  locked: boolean;
  onChanged: () => void;
  onError: (message: string) => void;
};

/**
 * The notes on a set or on one of its requirements. Like the rest of a set they are written in Draft
 * only; outside it the list stays readable and every row says it is locked.
 */
const NotesSection = ({
  requirementSetId,
  requirementId,
  notes,
  options,
  locked,
  onChanged,
  onError,
}: NotesSectionProps) => {
  const { formatMessage: t } = useIntl();
  const { requirementSetService } = useGlobalContext();
  const [editing, setEditing] = useState<{ note?: IRequirementNoteDetail }>();
  const [deleting, setDeleting] = useState<IRequirementNoteDetail>();

  const deleteNote = async () => {
    const noteId = deleting?.id;
    if (!noteId) return;

    // Closed before the call, so a second click on confirm has nothing left to delete.
    setDeleting(undefined);

    try {
      ensureOk(await requirementSetService.deleteRequirementNote(noteId));
      onChanged();
    } catch (error) {
      onError(translateError(error, t));
    }
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "typeLabel",
      type: "text",
      label: t({ id: "noteType" }),
    },
    {
      accessor: "title",
      type: "text",
      label: t({ id: "titleLabel" }),
    },
    {
      accessor: "content",
      type: "text",
      label: t({ id: "contentLabel" }),
    },
    {
      accessor: "actions",
      align: "right",
      type: "node",
      noSort: true,
      label: t({ id: "actions" }),
      View: RowActionsCell,
    },
  ];

  const rows = notes.map((note) => ({
    ...note,
    id: note.id ?? "",
    typeLabel: noteTypeLabelKey(note.noteTypeEnumValue)
      ? t({ id: noteTypeLabelKey(note.noteTypeEnumValue) })
      : note.noteTypeName,
    ...(locked
      ? {}
      : {
          onEdit: () => setEditing({ note }),
          onDelete: () => setDeleting(note),
        }),
  }));

  return (
    <>
      <Accordion sx={styles.notesAccordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            {t({ id: "notes" })} ({notes.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={styles.sectionActions}>
            <Box />
            {!locked && (
              <Button size="small" startIcon={<AddIcon />} onClick={() => setEditing({})}>
                {t({ id: "addNote" })}
              </Button>
            )}
          </Box>

          {notes.length === 0 ? (
            <Alert severity="info">{t({ id: "noNotes" })}</Alert>
          ) : (
            <SortableTable cells={cells} rows={rows} dense defaultOrderBy="sortOrder" />
          )}
        </AccordionDetails>
      </Accordion>

      {editing && (
        <NoteDialog
          requirementSetId={requirementSetId}
          requirementId={requirementId}
          note={editing.note}
          options={options}
          open
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            onChanged();
          }}
        />
      )}

      <DeleteEntityDialog
        open={!!deleting}
        dialogTitleKey="deleteNoteQuestion"
        rawContent={t({ id: "deleteNoteWarning" }, { title: deleting?.title ?? "" })}
        onCancel={() => setDeleting(undefined)}
        handleDelete={deleteNote}
      />
    </>
  );
};

export default NotesSection;
